/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import PromiseUtil from '../promise/promise-util';

/**
 * @import { FileSystemName } from '../file/system'
 * @import { Logger } from '../logger/logger'
 * @import { LogicalUnitMapper } from '../logical-unit-mapper/'
 */

/**
 * @param {LogicalUnitMapper} logicalUnitMapper
 * @param {Set<FileSystemName>} requiredUnits
 * @param {number} timeout Maximální čas v ms čekání na připojení externích jednotek.
 * @param {Logger} logger
 * @returns {Promise<void>}
 */
export async function waitForStorage(
  logicalUnitMapper,
  requiredUnits,
  timeout,
  logger
) {
  const delay = 500;
  // Opkauje se dokud není externí jednotka připojena
  // nebo není dosaženo maximálního počtu opakování.
  await PromiseUtil.retry(
    async () => {
      const isAvailable = await isUnitsAvailable(
        logicalUnitMapper,
        requiredUnits
      );
      if (!isAvailable) {
        logicalUnitMapper.refresh();
        throw new Error('entered units not available');
      }
    },
    { initialDelayMs: delay, max: Math.floor(timeout / delay) }
  ).catch((error) =>
    logger.error(
      new Error(
        `external units are required, but not available on device: ${error.message}`
      )
    )
  );
}

/**
 * Zkontroluje, zda-li jsou dostupné jednotky ze zadané množiny.
 * @param {LogicalUnitMapper} logicalUnitMapper
 * @param {Set<FileSystemName>} requiredUnits
 * @returns {Promise<boolean>}
 * @throws {Error} Pokud nejsou všechny jednotky ve vstupní množině dostupné.
 */
export async function isUnitsAvailable(logicalUnitMapper, requiredUnits) {
  const availableLogicalUnits =
    await logicalUnitMapper.getAvailableLogicalUnits();
  return isSubset(requiredUnits, new Set(Object.keys(availableLogicalUnits)));
}

// TODO: Vyčlenit do samostatného souboru,
// pokud by bylo potřeba testovat podmnožinu.
/**
 * @param {Set} setA
 * @param {Set} setB
 * @returns {boolean}
 */
function isSubset(setA, setB) {
  return [...setA].every((value) => setB.has(value));
}
